/**
 * Entity Selectors
 *
 * This file provides convenient selectors for accessing normalized entity data
 * from RTK Query cache. Entity adapters normalize collections by ID for O(1) lookups.
 *
 * Usage in components:
 *
 * // Get all members as an array
 * const members = useAppSelector(selectAllMembers);
 *
 * // Get a specific member by ID
 * const member = useAppSelector(state => selectMemberById(state, memberId));
 *
 * // Get all member IDs
 * const memberIds = useAppSelector(selectMemberIds);
 *
 * // Get member entities as a dictionary { [id]: member }
 * const memberEntities = useAppSelector(selectMemberEntities);
 *
 * Benefits:
 * - O(1) lookups instead of O(n) array.find()
 * - Memoized selectors prevent unnecessary re-renders
 * - Consistent normalized data structure
 */

import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../configureStore';
import type { MemberSummary, GolfEventSummary, TeamCompetition } from '../types/api.types';
import type {
  MemberSummaryEntityState,
  GolfEventSummaryEntityState,
  TeamCompetitionEntityState,
} from '../types/entity.types';
import {
  membersAdapter,
  eventsAdapter,
  teamCompetitionsAdapter,
  memberSelectors as baseMemberSelectors,
  eventSelectors as baseEventSelectors,
  teamCompetitionSelectors as baseTeamCompetitionSelectors,
} from './api';

// ===========================
// Helper to get query data from RTK Query cache
// ===========================

/**
 * Get the cached data for a specific RTK Query endpoint
 * @param state - Redux state
 * @param endpointName - Name of the endpoint (e.g., 'getMemberSummary')
 * @param args - Arguments passed to the query (undefined for no args)
 * @returns The cached data or undefined
 */
const selectQueryData = <T = unknown>(
  state: RootState,
  endpointName: string,
  args: unknown = undefined
): T | undefined => {
  const queryKey = args === undefined ? `${endpointName}(undefined)` : `${endpointName}(${JSON.stringify(args)})`;
  // Access RTK Query cache - the state shape is defined by RTK Query
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (state as any).api?.queries?.[queryKey]?.data as T | undefined;
};

// ===========================
// Member Selectors
// ===========================

/**
 * Select member entity state from cache
 */
const selectMemberEntityState = (state: RootState): MemberSummaryEntityState =>
  selectQueryData(state, 'getMemberSummary') || membersAdapter.getInitialState();

/**
 * Select all members as an array (sorted by name)
 */
export const selectAllMembers = createSelector([selectMemberEntityState], (entityState): MemberSummary[] =>
  baseMemberSelectors.selectAll(entityState)
);

/**
 * Select a specific member by ID
 * @param state - Redux state
 * @param memberId - Member ID
 * @returns Member object or undefined
 */
export const selectMemberById = createSelector(
  [selectMemberEntityState, (_state: RootState, memberId: string) => memberId],
  (entityState, memberId) => baseMemberSelectors.selectById(entityState, memberId) as MemberSummary | undefined
);

/**
 * Select all member IDs as an array
 */
export const selectMemberIds = createSelector(
  [selectMemberEntityState],
  (entityState): Array<string> => baseMemberSelectors.selectIds(entityState)
);

/**
 * Select member entities as a dictionary { [id]: member }
 */
export const selectMemberEntities = createSelector(
  [selectMemberEntityState],
  (entityState): Record<string, MemberSummary> =>
    baseMemberSelectors.selectEntities(entityState) as Record<string, MemberSummary>
);

/**
 * Select total number of members
 */
export const selectMemberCount = createSelector([selectMemberEntityState], (entityState): number =>
  baseMemberSelectors.selectTotal(entityState)
);

// ===========================
// Golf Event Selectors
// ===========================

/**
 * Select event entity state from cache
 */
const selectEventEntityState = (state: RootState): GolfEventSummaryEntityState =>
  selectQueryData(state, 'getGolfEventSummary') || eventsAdapter.getInitialState();

/**
 * Select all events as an array (sorted by date, newest first)
 */
export const selectAllEvents = createSelector([selectEventEntityState], (entityState): GolfEventSummary[] =>
  baseEventSelectors.selectAll(entityState)
);

/**
 * Select a specific event by ID
 * @param state - Redux state
 * @param eventId - Event ID
 * @returns Event object or undefined
 */
export const selectEventById = createSelector(
  [selectEventEntityState, (_state: RootState, eventId: string) => eventId],
  (entityState, eventId) => baseEventSelectors.selectById(entityState, eventId) as GolfEventSummary | undefined
);

/**
 * Select all event IDs as an array
 */
export const selectEventIds = createSelector(
  [selectEventEntityState],
  (entityState): Array<string> => baseEventSelectors.selectIds(entityState)
);

/**
 * Select event entities as a dictionary { [id]: event }
 */
export const selectEventEntities = createSelector(
  [selectEventEntityState],
  (entityState): Record<string, GolfEventSummary> =>
    baseEventSelectors.selectEntities(entityState) as Record<string, GolfEventSummary>
);

/**
 * Select events filtered by series
 * @param state - Redux state
 * @param series - Series name (e.g., 'Summer Tour')
 * @returns Filtered events
 */
export const selectEventsBySeries = createSelector(
  [selectAllEvents, (_state: RootState, series: string) => series],
  (events, series): GolfEventSummary[] => events.filter((event) => event.series === series)
);

// ===========================
// Team Competition Selectors
// ===========================

/**
 * Select team competition entity state from cache
 * @param withCompetitors - Whether to select from cache with competitors data
 */
const selectTeamCompetitionEntityState = (state: RootState, withCompetitors?: boolean): TeamCompetitionEntityState => {
  const args = withCompetitors ? { withCompetitors } : undefined;
  return selectQueryData(state, 'getTeamCompetitions', args) || teamCompetitionsAdapter.getInitialState();
};

/**
 * Select all team competitions as an array (sorted by date, newest first)
 * @param withCompetitors - Whether to select from cache with competitors data
 */
export const selectAllTeamCompetitions = createSelector(
  [(state: RootState) => state, (_state: RootState, withCompetitors?: boolean) => withCompetitors],
  (state, withCompetitors): TeamCompetition[] => {
    const entityState = selectTeamCompetitionEntityState(state, withCompetitors);
    return baseTeamCompetitionSelectors.selectAll(entityState);
  }
);

/**
 * Select a specific team competition by ID
 * @param state - Redux state
 * @param compId - Competition ID
 * @param withCompetitors - Whether to select from cache with competitors data
 * @returns Competition object or undefined
 */
export const selectTeamCompetitionById = createSelector(
  [
    (state: RootState) => state,
    (_state: RootState, compId: string, _withCompetitors?: boolean) => compId,
    (_state: RootState, _compId: string, withCompetitors?: boolean) => withCompetitors,
  ],
  (state, compId, withCompetitors) => {
    const entityState = selectTeamCompetitionEntityState(state, withCompetitors);
    return baseTeamCompetitionSelectors.selectById(entityState, compId) as TeamCompetition | undefined;
  }
);

/**
 * Select all team competition IDs as an array
 */
export const selectTeamCompetitionIds = createSelector(
  [selectTeamCompetitionEntityState],
  (entityState): Array<string> => baseTeamCompetitionSelectors.selectIds(entityState)
);

/**
 * Select team competition entities as a dictionary { [id]: competition }
 */
export const selectTeamCompetitionEntities = createSelector(
  [selectTeamCompetitionEntityState],
  (entityState): Record<string, TeamCompetition> =>
    baseTeamCompetitionSelectors.selectEntities(entityState) as Record<string, TeamCompetition>
);
